import { useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import drivingApi from '../services/driving';

function isValidIdentityCardNumber(value) {
	return /^\d{9,12}$/.test((value || '').trim());
}

function formatDate(value) {
	if (!value) return '-';
	const parsedDate = new Date(value);
	if (Number.isNaN(parsedDate.getTime())) return value;
	return parsedDate.toLocaleString('vi-VN', { hour12: false });
}

function DisplayField({ label, value }) {
	return (
		<Col md={6} className='mb-3'>
			<div style={{ fontSize: 13, opacity: 0.75 }}>{label}</div>
			<div style={{ fontWeight: 600, wordBreak: 'break-word' }}>{value || '-'}</div>
		</Col>
	);
}

export default function DrivingPaymentPage() {
	const searchParams = new URLSearchParams(window.location.search);
	const centerId = searchParams.get('centerId') || '';
	const [identityCardNumber, setIdentityCardNumber] = useState(searchParams.get('identityCardNumber') || '');
	const [loading, setLoading] = useState(false);
	const [lookupResult, setLookupResult] = useState(null);
	const [requestError, setRequestError] = useState('');

	const identityCardError = useMemo(() => {
		if (!identityCardNumber) return '';
		if (!isValidIdentityCardNumber(identityCardNumber)) return 'Số căn cước công dân phải gồm 9-12 chữ số.';
		return '';
	}, [identityCardNumber]);

	const handleLookup = async (event) => {
		event.preventDefault();

		const trimmedIdentityCardNumber = identityCardNumber.trim();
		if (!trimmedIdentityCardNumber) {
			setRequestError('Vui lòng nhập Số căn cước công dân để tra cứu.');
			setLookupResult(null);
			return;
		}

		if (!isValidIdentityCardNumber(trimmedIdentityCardNumber)) {
			setRequestError('Định dạng Số căn cước công dân không hợp lệ. Vui lòng nhập 9-12 chữ số.');
			setLookupResult(null);
			return;
		}

		setLoading(true);
		setRequestError('');

		try {
			const response = await drivingApi.lookupPaymentByIdentityCard(trimmedIdentityCardNumber, centerId);
			const payload = response?.data || {};
			setLookupResult({
				found: Boolean(payload?.found),
				identityCardNumber: payload?.identityCardNumber || trimmedIdentityCardNumber,
				record: payload?.record || null,
			});
		} catch (error) {
			const statusCode = error?.response?.status;
			if (statusCode === 400) {
				setRequestError('Thiếu hoặc sai tham số identityCardNumber.');
			} else {
				setRequestError('Không thể tra cứu dữ liệu lúc này. Vui lòng thử lại sau.');
			}
			setLookupResult(null);
		} finally {
			setLoading(false);
		}
	};

	const record = lookupResult?.record;

	return (
		<div
			style={{
				minHeight: '100vh',
				background: 'radial-gradient(circle at 10% 20%, #d4f6f3 0%, #f6fef7 35%, #fffaf0 100%)',
				padding: '32px 0 48px',
			}}
		>
			<Container>
				<Row className='justify-content-center'>
					<Col lg={9}>
						<Card
							className='border-0 shadow-lg overflow-hidden'
							style={{ borderRadius: 20, background: 'linear-gradient(145deg, #ffffff, #f6fffe)' }}
						>
							<Card.Body className='p-4 p-md-5'>
								<div className='d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4'>
									<div>
										<h1 className='h3 mb-1' style={{ color: '#0f5132', fontWeight: 700 }}>
											Tra cứu lệ phí sát hạch GPLX
										</h1>
										<div className='text-muted'>
											Nhập Số căn cước công dân để kiểm tra thông tin và mã QR thanh toán.
										</div>
									</div>
								</div>

								<Form onSubmit={handleLookup}>
									<Row className='g-3 align-items-end'>
										<Col md={9}>
											<Form.Label className='fw-semibold'>Số căn cước công dân</Form.Label>
											<Form.Control
												type='text'
												inputMode='numeric'
												placeholder='Ví dụ: 012345678901'
												value={identityCardNumber}
												onChange={(event) => {
													const digitsOnlyValue = event.target.value.replace(/\D/g, '');
													setIdentityCardNumber(digitsOnlyValue);
													if (requestError) setRequestError('');
												}}
												maxLength={12}
												isInvalid={Boolean(identityCardError)}
											/>
											<Form.Control.Feedback type='invalid'>
												{identityCardError}
											</Form.Control.Feedback>
										</Col>
										<Col md={3}>
											<Button type='submit' className='w-100 fw-semibold' variant='success' disabled={loading}>
												{loading ? (
													<>
														<Spinner size='sm' className='me-2' />
														Đang tìm
													</>
												) : (
													'Tra cứu'
												)}
											</Button>
										</Col>
									</Row>
								</Form>

								{requestError && (
									<Alert variant='danger' className='mt-4 mb-0'>
										{requestError}
									</Alert>
								)}

								{lookupResult && !lookupResult.found && !requestError && (
									<Alert variant='warning' className='mt-4 mb-0'>
										Chưa tìm thấy thông tin thanh toán cho số căn cước công dân: <strong>{lookupResult.identityCardNumber}</strong>
									</Alert>
								)}

								{lookupResult?.found && record && (
									<Card className='mt-4 border-0' style={{ background: '#f8fffd', borderRadius: 16 }}>
										<Card.Body className='p-4'>
											<Row>
												<Col lg={8}>
													<h2 className='h5 mb-3' style={{ color: '#0f5132' }}>Thông tin học viên</h2>
													<Row>
														<DisplayField label='Họ tên' value={record.studentName} />
														<DisplayField label='Số căn cước công dân' value={record.identityCardNumber} />
														<DisplayField label='Ngày sinh' value={record.dateOfBirth} />
														<DisplayField label='Hạng GPLX' value={record.licenseClass} />
														<DisplayField label='Số tiền' value={record.amount ? `${record.amount} VND` : '-'} />
														<DisplayField label='Cập nhật lúc' value={formatDate(record.updatedAt)} />
													</Row>
												</Col>

												<Col lg={4} className='mt-3 mt-lg-0'>
													<div
														className='h-100 d-flex flex-column align-items-center justify-content-center p-3'
														style={{ background: '#fff', border: '1px solid #d8f3e7', borderRadius: 14 }}
													>
														<div className='fw-semibold mb-3'>QR thanh toán</div>
														{record.qrImageUrl ? (
															<img
																src={record.qrImageUrl}
																alt='QR thanh toán GPLX'
																style={{ width: '100%', maxWidth: 220, borderRadius: 12, border: '1px solid #e9ecef' }}
															/>
														) : (
															<Alert variant='secondary' className='mb-0 w-100 text-center'>
																Chưa có QR trong dữ liệu
															</Alert>
														)}
													</div>
												</Col>
											</Row>
										</Card.Body>
									</Card>
								)}
							</Card.Body>
						</Card>
					</Col>
				</Row>
			</Container>
		</div>
	);
}
